import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Typography, Button, Box, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Alert, CircularProgress, Tabs, Tab,
  List, ListItem, Chip, Paper
} from '@mui/material';
import { Add, Edit, Delete, Link as LinkIcon, Poll, Article } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = API_BASE_URL;

const AdminNetworkNotificationsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [news, setNews] = useState([]);
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'news' or 'poll'
  const [editingId, setEditingId] = useState(null);

  // News form
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    link: ''
  });

  // Poll form
  const [pollForm, setPollForm] = useState({
    question: '',
    options: ['', ''],
    expiresAt: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = Cookies.get('idToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [newsRes, pollsRes] = await Promise.all([
        axios.get(`${API_BASE}/network/news/list`),
        axios.get(`${API_BASE}/network/polls/list`, config)
      ]);

      setNews(newsRes.data.news || []);
      setPolls(pollsRes.data.polls || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // ========== NEWS HANDLERS ==========

  const handleOpenNewsDialog = (newsItem = null) => {
    setDialogType('news');
    if (newsItem) {
      setEditingId(newsItem.id);
      setNewsForm({
        title: newsItem.title || '',
        content: newsItem.content || '',
        link: newsItem.link || ''
      });
    } else {
      setEditingId(null);
      setNewsForm({ title: '', content: '', link: '' });
    }
    setOpenDialog(true);
  };

  const handleNewsSubmit = async () => {
    if (!newsForm.title || !newsForm.content) {
      setError('Title and content are required');
      return;
    }

    try {
      const token = Cookies.get('idToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingId) {
        await axios.put(`${API_BASE}/network/news/update/${editingId}`, newsForm, config);
      } else {
        await axios.post(`${API_BASE}/network/news/create`, newsForm, config);
      }

      setSuccess(editingId ? 'News updated!' : 'News created!');
      setOpenDialog(false);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save news');
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm('Delete this news post?')) return;

    try {
      const token = Cookies.get('idToken');
      await axios.delete(`${API_BASE}/network/news/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('News deleted!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete news');
    }
  };

  // ========== POLL HANDLERS ==========

  const handleOpenPollDialog = () => {
    setDialogType('poll');
    setEditingId(null);
    setPollForm({ question: '', options: ['', ''], expiresAt: '' });
    setOpenDialog(true);
  };

  const handlePollOptionChange = (index, value) => {
    const newOptions = [...pollForm.options];
    newOptions[index] = value;
    setPollForm({ ...pollForm, options: newOptions });
  };

  const handleAddPollOption = () => {
    setPollForm({ ...pollForm, options: [...pollForm.options, ''] });
  };

  const handleRemovePollOption = (index) => {
    if (pollForm.options.length > 2) {
      const newOptions = pollForm.options.filter((_, i) => i !== index);
      setPollForm({ ...pollForm, options: newOptions });
    }
  };

  const handlePollSubmit = async () => {
    if (!pollForm.question) {
      setError('Question is required');
      return;
    }

    const validOptions = pollForm.options.filter(o => o.trim());
    if (validOptions.length < 2) {
      setError('At least 2 options required');
      return;
    }

    try {
      const token = Cookies.get('idToken');
      await axios.post(`${API_BASE}/network/polls/create`, {
        question: pollForm.question,
        options: validOptions,
        expiresAt: pollForm.expiresAt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Poll created!');
      setOpenDialog(false);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create poll');
    }
  };

  const handleDeletePoll = async (id) => {
    if (!window.confirm('Delete this poll and all votes?')) return;

    try {
      const token = Cookies.get('idToken');
      await axios.delete(`${API_BASE}/network/polls/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Poll deleted!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete poll');
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">📡 Network Notifications</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => activeTab === 0 ? handleOpenNewsDialog() : handleOpenPollDialog()}
        >
          {activeTab === 0 ? 'Add News' : 'Add Poll'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
          <Tab icon={<Article />} label="News Posts" />
          <Tab icon={<Poll />} label="Polls" />
        </Tabs>
      </Paper>

      {/* NEWS TAB */}
      {activeTab === 0 && (
        <Box>
          {news.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">No news posts yet</Typography>
              </CardContent>
            </Card>
          ) : (
            news.map((item) => (
              <Card key={item.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {item.content}
                      </Typography>
                      {item.link && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <LinkIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">{item.link}</Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {item.date} • {item.author}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenNewsDialog(item)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteNews(item.id)}>
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      {/* POLLS TAB */}
      {activeTab === 1 && (
        <Box>
          {polls.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">No polls yet</Typography>
              </CardContent>
            </Card>
          ) : (
            polls.map((poll) => (
              <Card key={poll.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>{poll.question}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {poll.options.map((option, idx) => (
                          <Chip
                            key={idx}
                            label={`${option} (${poll.voteCounts?.[option] || 0})`}
                            size="small"
                            color={poll.voteCounts?.[option] > 0 ? 'primary' : 'default'}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Votes: {poll.totalVotes || 0} • Created: {poll.created?.split('T')[0]}
                      </Typography>
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleDeletePoll(poll.id)}>
                      <Delete />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      {/* DIALOGS */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'news' ? (editingId ? 'Edit News' : 'Add News') : 'Create Poll'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'news' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={newsForm.title}
                onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                required
              />
              <TextField
                label="Content"
                fullWidth
                multiline
                rows={4}
                value={newsForm.content}
                onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })}
                required
              />
              <TextField
                label="Link (Optional)"
                fullWidth
                value={newsForm.link}
                onChange={(e) => setNewsForm({ ...newsForm, link: e.target.value })}
                placeholder="https://..."
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Question"
                fullWidth
                value={pollForm.question}
                onChange={(e) => setPollForm({ ...pollForm, question: e.target.value })}
                required
              />
              {pollForm.options.map((option, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label={`Option ${idx + 1}`}
                    fullWidth
                    value={option}
                    onChange={(e) => handlePollOptionChange(idx, e.target.value)}
                    required
                  />
                  {pollForm.options.length > 2 && (
                    <IconButton color="error" onClick={() => handleRemovePollOption(idx)}>
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={handleAddPollOption}>
                Add Option
              </Button>
              <TextField
                label="Expires At (Optional)"
                type="date"
                fullWidth
                value={pollForm.expiresAt}
                onChange={(e) => setPollForm({ ...pollForm, expiresAt: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={dialogType === 'news' ? handleNewsSubmit : handlePollSubmit}
            variant="contained"
          >
            {dialogType === 'news' ? (editingId ? 'Update' : 'Create') : 'Create Poll'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminNetworkNotificationsPage;
